import * as React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import { ContactForm } from "@/components/ui/contact-form";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";
import { SeoHead } from "@/components/SeoHead";
import { getSeoCopy } from "@/i18n/seoCopy";
import { useI18n } from "@/i18n";
import { Footer } from "@/components/Footer";
import { GoogleMap } from "@/components/GoogleMap";
import { Mail, Phone, MapPin, Clock, Award, Shield, TrendingUp, Globe, MessageCircle } from "lucide-react";
import aureumLogo from "@/assets/aureum-logo-new.png";
import officeHeadquarters from "@/assets/office-headquarters-logo.jpg";
import { trackPhoneClick, trackWhatsAppClick } from "@/components/ConversionTracking";

const Contact = () => {
  const { t, language } = useI18n();
  const seo = getSeoCopy("contact", language);
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SeoHead route="contact" />
      <Navigation />
      
      {/* Hero Section */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12 bg-white">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="mb-6 sm:mb-8">
            <img loading="lazy" decoding="async" 
              src={aureumLogo} 
              alt="Aureum VII Trading Logo" 
              className="h-12 sm:h-16 w-auto mx-auto mb-4 sm:mb-6"
            />
          </div>
          <h1 className="font-playfair text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold mb-6 sm:mb-8 text-primary px-2">
            {seo.h1}
          </h1>
          <p className="font-inter text-lg sm:text-xl md:text-2xl text-muted-foreground leading-relaxed max-w-3xl mx-auto px-4">
            Request a private demonstration, a homeowner quote, installation information or trade and project pricing.
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-24 px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl">
          <BreadcrumbNav items={[
            { label: "Home", href: "/" },
            { label: "Contact" }
          ]} />
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            
            {/* Contact Details */}
            <div className="space-y-12">
              <div>
                <h2 className="font-playfair text-3xl font-bold mb-8 text-primary">Business Contact</h2>
            <p className="font-inter text-lg text-muted-foreground leading-relaxed mb-8">
              Aureum VII Trading supports homeowners and professional buyers from Marbella. Tell us whether you need a private REM7 demonstration, a one-unit quote, installation information or project pricing.
            </p>
              </div>

              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-playfair text-lg font-semibold mb-2">Business Email</h3>
                    <a 
                      href="mailto:info@aureum7trading.com" 
                      className="font-inter text-accent hover:text-accent/80 transition-colors"
                    >
                      info@aureum7trading.com
                    </a>
                    <p className="font-inter text-sm text-muted-foreground">Homeowner and professional enquiries</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-playfair text-lg font-semibold mb-2">EU Business Line</h3>
                    <a 
                      href="tel:+34688495309"
                      onClick={trackPhoneClick}
                      className="font-inter text-accent hover:text-accent/80 transition-colors"
                    >
                      +34 688 49 53 09
                    </a>
                    <p className="font-inter text-sm text-muted-foreground">Monday - Friday, 9:00 - 18:00 CET</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-playfair text-lg font-semibold mb-2">WhatsApp Business</h3>
                    <a 
                      href="https://wa.me/34688495309" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      onClick={trackWhatsAppClick}
                      className="font-inter text-accent hover:text-accent/80 transition-colors"
                    >
                      +34 688 49 53 09
                    </a>
                    <p className="font-inter text-sm text-muted-foreground">For REM7 product and project questions</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-playfair text-lg font-semibold mb-2">European Office</h3>
                    <p className="font-inter text-muted-foreground">Marbella, Spain (Virtual Office)</p>
                    <p className="font-inter text-sm text-muted-foreground">Servicing all EU markets</p>
                  </div>
                </div>
              </div>

              {/* Google Map */}
              <div className="mt-12">
                <h3 className="font-playfair text-xl font-semibold mb-4 text-primary">Find Us</h3>
                <GoogleMap height="350px" className="shadow-elegant" />
              </div>

              {/* Office Image */}
              <div className="mt-12">
                <div className="rounded-lg overflow-hidden shadow-elegant">
                  <img loading="lazy" decoding="async" 
                    src={officeHeadquarters} 
                    alt="Aureum VII Trading European Headquarters - Modern corporate building with distinctive architectural design featuring our golden brand logo"
                    className="w-full h-64 sm:h-80 object-cover"
                  />
                  <div className="p-6 bg-white border-t border-border/10">
                    <h3 className="font-playfair text-xl font-semibold mb-2 text-primary">About Our Business Model</h3>
                    <p className="font-inter text-muted-foreground">
                      REM7 is produced in China for import into Spain. Our Marbella-based team handles enquiries, product information and project coordination for European buyers.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-playfair text-lg font-semibold mb-2">Response Times</h3>
                    <p className="font-inter text-muted-foreground">We review each enquiry and respond with the relevant product or project information.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <ContactForm />
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
              <h2 className="font-playfair text-4xl font-bold mb-6 text-primary">Why Businesses Choose AVT</h2>
            <p className="font-inter text-lg text-muted-foreground max-w-3xl mx-auto">
              Transparent sourcing, competitive pricing, and full EU compliance for your smart toilet needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-accent/10 rounded-full flex items-center justify-center">
                <Award className="w-8 h-8 text-accent" />
              </div>
              <h3 className="font-playfair text-lg font-semibold mb-2">Transparent Sourcing</h3>
              <p className="font-inter text-sm text-muted-foreground">Direct partnerships with certified Chinese manufacturers</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-accent/10 rounded-full flex items-center justify-center">
                <Shield className="w-8 h-8 text-accent" />
              </div>
              <h3 className="font-playfair text-lg font-semibold mb-2">CE Certified</h3>
              <p className="font-inter text-sm text-muted-foreground">All products meet EU compliance standards</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-accent/10 rounded-full flex items-center justify-center">
                <Globe className="w-8 h-8 text-accent" />
              </div>
              <h3 className="font-playfair text-lg font-semibold mb-2">EU-Wide Shipping</h3>
              <p className="font-inter text-sm text-muted-foreground">Door-to-door delivery via DHL/FedEx in 4-6 weeks</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-accent/10 rounded-full flex items-center justify-center">
                <TrendingUp className="w-8 h-8 text-accent" />
              </div>
              <h3 className="font-playfair text-lg font-semibold mb-2">Innovation Leader</h3>
              <p className="font-inter text-sm text-muted-foreground">Award-winning design and technology</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}

      {/* CTA Section */}
      <section className="py-24 px-6 lg:px-12 bg-background">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-playfair text-4xl md:text-5xl font-bold mb-6">
            Ready for Elite Wellness?
          </h2>
          <p className="font-inter text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join discerning clients who trust AVT for sophisticated smart toilet systems and unparalleled European craftsmanship.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/smart-toilets">
              <Button className="bg-accent hover:bg-accent/90 text-accent-foreground font-inter font-semibold px-8 py-3 rounded-md shadow-gold">
                Explore Collections
              </Button>
            </Link>
            <a 
              href="https://wa.me/34688495309?text=Hello%2C%20I%27m%20interested%20in%20your%20luxury%20smart%20toilet%20collections.%20Could%20you%20please%20send%20me%20the%20catalog%3F" 
              target="_blank" 
              rel="noopener noreferrer"
              onClick={trackWhatsAppClick}
            >
              <Button variant="outline" className="font-inter font-semibold px-8 py-3 rounded-md">
                WhatsApp Catalog
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Contact;